/**
 * Whether the user's device supports fine-grained pointer
 */
export const supportsHover = matchMedia('(hover: hover)').matches

/**
 * Check whether the current browser supports a given selector
 *
 * @param {string} selector
 * @returns {boolean}
 */
export function supportsSelector(selector) {
  let style = document.createElement('style')
  document.head.appendChild(style)
  let sheet = style.sheet

  try {
    sheet.insertRule(`${selector} {}`)
    return true
  } catch {
    return false
  } finally {
    style.remove()
  }
}

/**
 * Replace HTML inside a string with entities
 *
 * @param {string} string
 * @returns {string}
 */
export function escapeHTML(string) {
  return String(string)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

/**
 * Get a v4 UUID
 *
 * @returns {string}
 */
export function uuid() {
  if ('randomUUID' in globalThis.crypto) {
    return globalThis.crypto.randomUUID()
  }

  let s4 = () =>
    Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1)

  return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4()
}

/**
 * Create a Node from an HTML template string, escaping all interpolated values, with special handling for:
 * - DOM Nodes (inserted as-is, retaining things like event handlers)
 * - functions (bound as event handlers and therefore are only allowed as on* attribute values)
 * - arrays (handles items as if they were each inserted on their own)
 *
 * @param {string|string[]} strings
 * @param {...unknown} values
 * @returns {Node}
 *
 * @example
 * ```
 * let button = fragment`<button onclick="${() => console.log('Clicked')}">Hit me!</button>`
 * ```
 */
export function fragment(strings, ...values) {
  if (typeof strings === 'string') {
    strings = [strings]
  }

  let replacedNodes = new Map()
  let replacedAttributes = new Map()
  let getReplacementTagHtml = id => `<template id="${id}"></template>`

  let getEscapedHtml = value => {
    let escapedString

    if (Array.isArray(value)) {
      return value.map(getEscapedHtml).join('')
    } else if (value instanceof Node) {
      let id = `replacement-${uuid()}`
      replacedNodes.set(id, value)
      escapedString = getReplacementTagHtml(id)
    } else if (typeof value === 'function') {
      let id = uuid()
      replacedAttributes.set(id, value)
      escapedString = id
    } else {
      escapedString = escapeHTML(value)
    }

    return escapedString
  }

  let template = document.createElement('template')
  let templateHtml = strings
    .reduce((carry, current, index) => carry + getEscapedHtml(values[index - 1]) + current)
    .trim()

  for (let [id] of replacedAttributes) {
    templateHtml = templateHtml.replaceAll(
      new RegExp(`((on[a-z]+)=(['"])${id}\\3)`, 'g'),
      `data-replace-${id}="$2"`,
    )
  }

  template.innerHTML = templateHtml

  // Replace placeholders
  for (let [id, element] of replacedNodes) {
    let placeholder = template.content.getElementById(id)

    if (placeholder) {
      placeholder.insertAdjacentElement('afterend', element)
      placeholder.remove()
    }
  }
  for (let [id, callback] of replacedAttributes) {
    let attribute = `data-replace-${id}`
    let placeholder = template.content.querySelector(`[${attribute}]`)
    let eventAttribute = placeholder.getAttribute(attribute)
    placeholder.removeAttribute(attribute)

    if (eventAttribute in placeholder) {
      placeholder[eventAttribute] = callback
    }
  }

  if (template.content.childNodes.length === 1) {
    return template.content.firstChild
  } else {
    return template.content
  }
}

let focusableSelectors = [
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
]

let focusableSelector = focusableSelectors.join(', ')

/**
 * Check if the given element is focusable
 *
 * @param {Element} element
 */
export function isFocusable(element) {
  if (!element.matches(focusableSelector)) return false
  if (element.tabIndex === -1) return false

  return true
}

/**
 * Get the next element in the DOM tree
 *
 * @param {Element} reference
 * @param {((element: Element) => boolean)|undefined} filter
 * @returns {Element|undefined}
 */
export function getNextElement(reference, filter) {
  let iterator = document.createNodeIterator(document.body, NodeFilter.SHOW_ELEMENT, {
    acceptNode(node) {
      // Reject the reference itself
      if (node === reference) return NodeFilter.FILTER_REJECT

      let position = node.compareDocumentPosition(reference)

      // Skip ancestors
      if (position & Node.DOCUMENT_POSITION_CONTAINED_BY) return NodeFilter.FILTER_SKIP

      // Skip predecessors
      if (position & Node.DOCUMENT_POSITION_FOLLOWING || position & Node.DOCUMENT_POSITION_CONTAINS)
        return NodeFilter.FILTER_REJECT

      // Skip invalid elements
      if (typeof filter === 'function' && !filter(node)) return NodeFilter.FILTER_SKIP

      return NodeFilter.FILTER_ACCEPT
    },
  })

  return iterator.nextNode() || undefined
}
